import React, { useEffect, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { Button, Modal, Input, TextArea, Message, DatePicker, Spin } from "../../BP2Components";
import IntlMessages from "../../../util/IntlMessages";
import { useIntl } from "react-intl";
import moment, { Moment } from "moment";
import { saveFormV2 } from "../../../services/restAPI/RestApi";
import projectConfig from "../../../projectConfig.json"; //@projectConfig is not working in tsx files
import { PlannerScripts } from "../../Planner/PlannerScripts";
import "./planMessageModal.less";
import {
   Icon_CasePlan,
   Icon_Close,
   Icon_ChevronLeft,
   Icon_ChevronRight,
   Icon_SandGlass,
   Icon_Lock,
   Icon_Unlock,
   Icon_Clock,
   Icon_Plan_Add,
   Icon_PlannedCalendar,
   Icon_Info,
   Icon_Plus,
   Icon_Check,
   Icon_Reschedule,
   Icon_CasePlanned,
} from "../../BP2Components/Icons/icons";
import AddComments from "../../Comments/AddComments";
import { getSuggestedDates } from "../../../services/restAPI/RestApi";
import { SuggestedDate } from "./types";
import { PlanMessageModalScripts } from "./PlanMessageModalScripts";
import { useHistory } from "react-router-dom";
import "moment/locale/pl";
import { getNameOfDayOfWeek } from "../../../util/Scripts";
import { useRegistryColorsContext } from "../../../context/RegistryContexts/RegistryColorsContext";
import { getCaseStatus } from "../../../util/getCaseStatus";
import ShowDate from "../../FormsV2/CasesList/SingleCase/ShowDate/ShowDate";
import clsx from "clsx";
import "../../CallManager/CallManager.less";

const formatDuration = (duration: number) => {
   const hours = Math.floor(duration / 60);
   const minutes = duration % 60;

   if (!hours && !minutes) {
      return `00:00h`;
   }

   if (minutes === 0) {
      return `${hours}:00h`;
   } else {
      return `${hours}:${minutes.toString().padStart(2, "0")}h`;
   }
};

type PlanMessageModalProps = {
   getCases?: (val: number) => void;
   setPageToDownload?: (val: number) => void;
   privateTags: string;
   messageId: number;
   start: string;
   duration: number;
   title: string;
   lockDate?: string;
   lockedDataAndTime?: boolean;
   deadlineDate?: string;
   comment?: string;
   tags: string;
   isInfiniteList?: boolean;
   replanIcon?: boolean;
   updateFunction?: () => void;
   activeCasesFolder?: string;
   calendarRef?: React.RefObject<HTMLDivElement>;
   isCallManager?: boolean;
   areStylesBasedOnCaseStatus?: boolean;
};

//There are some ts-ignores left, that's because we have date picker that can return sometimes moment and Date, we need to fix that if that's possible, to for expample always using moment dates.
const PlanMessageModal: React.FC<PlanMessageModalProps> = ({
   privateTags = "",
   messageId,
   start = null,
   duration,
   title = "",
   isInfiniteList = false,
   lockDate = "0",
   lockedDataAndTime = "0",
   deadlineDate,
   tags = "",
   replanIcon,
   updateFunction,
   activeCasesFolder,
   calendarRef,
   getCases,
   setPageToDownload,
   isCallManager = false,
   areStylesBasedOnCaseStatus,
}) => {
   const [isOpen, setIsOpen] = useState<boolean>(false);

   // This object holds all the data that user will change in the planned message, if he saves.
   const [plannedDetails, setPlannedDetails] = useState<{
      tags: string;
      privateTags: string;
      startTime: Moment | null;
      startDate: Moment | null;
      duration: number;
      lockedDataAndTime: boolean;
      lockDate: boolean;
      comment: string;
   }>({
      tags: tags,
      privateTags: privateTags,
      startTime: moment(start),
      startDate: moment(start),
      duration,
      lockedDataAndTime: lockedDataAndTime === "0" ? false : Boolean(lockedDataAndTime),
      lockDate: lockDate === "0" ? false : Boolean(lockDate),
      comment: "",
   });
   const [openAddTagInput, setOpenAddTagInput] = useState<boolean>(false);
   const [tagValue, setTagValue] = useState<string>("");
   const [editingIdx, setEditingIdx] = useState<null | number>(null);
   const [effortTime, setEffortTime] = useState<number>(0);
   const lastAutoDurationRef = useRef<number | null>(null);
   const [hover, setHover] = useState(false);

   // That's an array of suggestedDates to plan for a user, provided by api.
   const [suggestedDates, setSuggestedDates] = useState<SuggestedDate[]>([]);

   // used to check if current selected day was changed
   const [currentSuggestedDay, setCurrentSuggestedDay] = useState<string>("");

   // This an date that is visible for user in a window with suggested dates.
   const [displayedSuggestedDate, setDisplayedSuggestedDate] = useState<SuggestedDate | null>(null);

   const [timeUsedToday, setTimeUsedToday] = useState<string>("0:00");
   const [automaticTags, setAutomaticTags] = useState<boolean>(false);
   const [onSendComment, setOnSendComment] = useState<boolean>(false);

   //this prevents requests to be send multiple times
   const [areSuggestedDatesFetched, setAreSuggestedDatesFetched] = useState<boolean>(false);
   const [arePreviousSlotsAvailable, setArePreviousSlotsAvailable] = useState<boolean>(false);

   const intl = useIntl();
   const history = useHistory();

   // This is the data that is visible on the planMessageModal BUTTON. It uses privded data from props or plannedDetails if user saved changes.
   const [visiblePlannedData, setVisiblePlannedData] = useState({
      plannedTime: start && !start.includes("0000-00-00") ? start : "",
      duration: duration,
      lockedDataAndTime: lockedDataAndTime === "0" ? false : Boolean(lockedDataAndTime),
      lockDate: lockDate === "0" ? false : Boolean(lockDate),
      tags: tags,
      privateTags: privateTags,
   });

   const registryColors = useRegistryColorsContext();

   const caseStatus = getCaseStatus(start, deadlineDate, duration);

   const buttonStyles = registryColors[caseStatus];

   //TODO make another function for start_time, and then callback to this funciton in next task
   const updatePlannedDetails = (
      value: string | number | boolean | Moment | null | Date,
      field: string,
      areNewSuggestedDatesRequired?: boolean | undefined,
   ) => {
      setPlannedDetails((prevPlannedDetails) => ({ ...prevPlannedDetails, [field]: value }));
      areNewSuggestedDatesRequired && debouncedFetchSuggestedDates();
   };

   const debouncedFetchSuggestedDates = useDebouncedCallback(() => {
      fetchSuggestedDates(0, {
         suggestedDate: plannedDetails?.startDate?.format("YYYY-MM-DD") || "",
         suggestedTime: plannedDetails?.startTime?.format("HH:mm:ss") || "",
      });
   }, 1000);

   useEffect(() => {
      const handleClick = () => {
         setIsOpen(true);
      };
      if (calendarRef && calendarRef.current) {
         calendarRef.current.addEventListener("click", handleClick);
      }
      return () => {
         if (calendarRef && calendarRef.current) {
            calendarRef.current.removeEventListener("click", handleClick);
         }
      };
   }, [calendarRef]);

   useEffect(() => {
      if (!isOpen) {
         setPlannedDetails({
            tags: visiblePlannedData.tags,
            privateTags: visiblePlannedData.privateTags,
            startTime:
               moment(visiblePlannedData.plannedTime).format("YYYY-MM-DD") !== "Invalid date"
                  ? moment(visiblePlannedData.plannedTime)
                  : moment(),
            startDate:
               moment(visiblePlannedData.plannedTime).format("YYYY-MM-DD") !== "Invalid date"
                  ? moment(visiblePlannedData.plannedTime)
                  : moment(),
            duration:
               +visiblePlannedData.duration !== 0 ? +visiblePlannedData.duration : effortTime,
            lockedDataAndTime: visiblePlannedData.lockedDataAndTime,
            lockDate: visiblePlannedData.lockDate,
            comment: "",
         });
      }
   }, [
      privateTags,
      messageId,
      start,
      duration,
      title,
      isInfiniteList,
      lockDate,
      lockedDataAndTime,
      tags,
      isOpen,
   ]);

   const fetchSuggestedDates = (actionType: number, startDate: SuggestedDate) => {
      setAreSuggestedDatesFetched(true);
      getSuggestedDates(
         messageId,
         startDate?.suggestedDate,
         startDate?.suggestedTime,
         actionType,
         plannedDetails?.duration,
      )
         .then((res) => {
            setEffortTime(res.data.getSuggestedDates.effortTime);
            const responseSuggestedDates = PlanMessageModalScripts.createSuggestedDatesFromResponse(
               res,
               actionType,
               suggestedDates,
            );

            if (actionType === 0) {
               PlanMessageModalScripts.updateTimeUsedToday(
                  res?.data?.getSuggestedDates?.plannedTime,
                  setTimeUsedToday,
               );
               setDisplayedSuggestedDate(
                  responseSuggestedDates[responseSuggestedDates.length - 10],
               );
            }
            if (actionType !== 1) {
               setArePreviousSlotsAvailable(
                  res?.data?.getSuggestedDates?.previousFreeSlots.length < 9,
               );
            }
            setSuggestedDates(responseSuggestedDates);
            setAreSuggestedDatesFetched(false);
         })
         .catch((err) => console.log(err));
   };

   const showTags = (): void => {
      setAutomaticTags(!automaticTags);
   };

   useEffect(() => {
      if (isOpen) {
         setSuggestedDates([]);
         fetchSuggestedDates(0, {
            suggestedDate: plannedDetails?.startDate?.format("YYYY-MM-DD") || "",
            suggestedTime: plannedDetails?.startTime?.format("HH:mm:ss") || "",
         });
      }
   }, [isOpen]);

   useEffect(() => {
      if (effortTime > 0) {
         setPlannedDetails((prev) => {
            if (
               prev.duration === 0 ||
               prev.duration == null ||
               prev.duration === lastAutoDurationRef.current
            ) {
               lastAutoDurationRef.current = effortTime;
               return { ...prev, duration: effortTime };
            }
            return prev;
         });
      }
   }, [effortTime, plannedDetails.duration]);

   useEffect(() => {
      if (effortTime > 0) {
         setVisiblePlannedData((prev) => {
            if (
               prev.duration === 0 ||
               (prev as any).duration == null ||
               prev.duration === lastAutoDurationRef.current
            ) {
               lastAutoDurationRef.current = effortTime;
               return { ...prev, duration: effortTime };
            }
            return prev;
         });
      }
   }, [effortTime, visiblePlannedData.duration]);

   const planMessage = () => {
      return new Promise<void>((resolve, reject) => {
         try {
            const saveFormId = projectConfig.planner?.planForm;
            const fieldsData = {
               extendedProps: {
                  messageId,
                  tags: plannedDetails.tags,
                  privateTags: plannedDetails.privateTags,
               },
               title,
            };
            const datePart = plannedDetails.startDate
               ? plannedDetails.startDate.format("YYYY-MM-DD")
               : "";
            const timePart = plannedDetails.startTime
               ? plannedDetails.startTime.format("HH:mm")
               : "";
            const fields = PlannerScripts.prepareObjectToPlan(
               fieldsData,
               `${datePart} ${timePart}`,
               plannedDetails.duration,
               Number(plannedDetails.lockDate),
               Number(plannedDetails.lockedDataAndTime),
            );

            setOnSendComment(true);

            Message.loading(intl.formatMessage({ id: "global.loading" }));

            saveFormV2(saveFormId, null /* noFormKey - support */, fields, messageId)
               .then(() => {
                  Message.destroy();
                  Message.success(intl.formatMessage({ id: "global.done" }));

                  setVisiblePlannedData({
                     plannedTime: `${datePart} ${timePart}:00`,
                     duration: plannedDetails.duration,
                     lockedDataAndTime: plannedDetails.lockedDataAndTime,
                     lockDate: plannedDetails.lockDate,
                     tags: plannedDetails.tags,
                     privateTags: plannedDetails.privateTags,
                  });
                  setIsOpen(false);
                  updateFunction && updateFunction();
                  resolve();
                  {
                     activeCasesFolder && history.push(`/cases/${activeCasesFolder}`);
                  }
                  if (getCases) {
                     getCases(1);
                  }
                  if (setPageToDownload) {
                     setPageToDownload(1);
                  }
               })
               .catch((error) => {
                  Message.destroy();
                  Message.error(intl.formatMessage({ id: "global.error" }));
                  reject(error);
               });
         } catch (error) {
            reject(error);
         }
      });
   };

   const handleChangeSelectedSuggestedDate = (value: number) => {
      const displayedSuggestedDateIndex = getDisplayedSuggestedDateIndex();
      if (
         displayedSuggestedDateIndex + value >= 0 &&
         displayedSuggestedDateIndex + value < suggestedDates.length
      ) {
         setDisplayedSuggestedDate(suggestedDates[displayedSuggestedDateIndex + value]);
      }
      if (
         displayedSuggestedDateIndex + value === suggestedDates.length - 2 &&
         !areSuggestedDatesFetched
      ) {
         fetchSuggestedDates(1, suggestedDates[suggestedDates.length - 1]);
      }
      if (
         displayedSuggestedDateIndex + value === 2 &&
         !areSuggestedDatesFetched &&
         !arePreviousSlotsAvailable
      ) {
         fetchSuggestedDates(2, suggestedDates[0]);
      }
   };

   const acceptSuggestedDate = () => {
      const newDate =
         displayedSuggestedDate?.suggestedDate + " " + displayedSuggestedDate?.suggestedTime;

      updatePlannedDetails(
         moment(PlanMessageModalScripts.splitDateTimeToTimestamp(newDate)),
         "startDate",
      );
      updatePlannedDetails(
         moment(PlanMessageModalScripts.splitDateTimeToTimestamp(newDate)),
         "startTime",
      );
   };

   const SuggestedDateInfo = () => {
      if (!displayedSuggestedDate) {
         return <Spin />;
      }
      const dayOfWeek = getNameOfDayOfWeek(displayedSuggestedDate.suggestedDate);
      const date = moment(displayedSuggestedDate.suggestedDate).format("DD.MM");

      // here we check if date changed. If so, we change "startTime" to 08:00 (new day, we want to start in from the morning)
      if (currentSuggestedDay !== dayOfWeek && currentSuggestedDay !== "") {
         setCurrentSuggestedDay(dayOfWeek);

         let value = moment(displayedSuggestedDate.suggestedDate);
         if (!value.isValid()) {
            value = moment();
         }

         updatePlannedDetails(value, "startDate", true);

         const updatedTime = value.clone().set("hour", 8).set("minute", 0);
         updatePlannedDetails(updatedTime, "startTime");
      }

      return (
         <b>
            {dayOfWeek + " " + date} <IntlMessages id="planner.since" />{" "}
            {displayedSuggestedDate.suggestedTime}
         </b>
      );
   };

   const splitDateAndTime = (type: string, time: string) => {
      if (time) {
         const split = time.split(" ");
         const date = [...split[0].split("-")].reverse().join().replace(/,/g, ".").slice(0, 5);
         const hours = split[1].slice(0, -3);

         if (type === "date") {
            return date;
         } else {
            return hours;
         }
      }
   };

   const getDisplayedSuggestedDateIndex = () => {
      return suggestedDates.findIndex(
         (date) =>
            date.suggestedDate === displayedSuggestedDate?.suggestedDate &&
            date.suggestedTime === displayedSuggestedDate?.suggestedTime,
      );
   };

   const displayedTime = () => {
      const { plannedTime, duration } = visiblePlannedData ?? {};

      if (!plannedTime) return "";

      const date = splitDateAndTime("date", plannedTime);
      const time = splitDateAndTime("time", plannedTime);
      const dur = formatDuration(duration);

      if (!date || !time) return "";

      return `${date} | ${time} (${dur})`;
   };

   const savePrivateTag = () => {
      setPlannedDetails((prev) => {
         const arr = (prev.privateTags || "")
            .split(",")
            .map((tag) => tag.trim())
            .filter(Boolean);

         const value = (tagValue || "").trim();
         if (!value) return prev;
         if (editingIdx !== null) {
            if (editingIdx >= 0 && editingIdx < arr.length) {
               arr[editingIdx] = value;
            }
         } else {
            arr.push(value);
         }

         return { ...prev, privateTags: arr.join(",") };
      });

      setOpenAddTagInput(false);
      setEditingIdx(null);
      setTagValue("");
   };

   const handleEdit = (tag: string, idx: number) => {
      if (idx < 0 || idx >= tagsToArray.length) {
         return;
      }
      setTagValue(tag);
      setEditingIdx(idx);
      setOpenAddTagInput(false);
   };

   const tagsToArray = React.useMemo(
      () =>
         (plannedDetails.privateTags || "")
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean),
      [plannedDetails.privateTags],
   );

   const removePrivateTag = (idx: number) => {
      setPlannedDetails((prev) => {
         const arr = (prev.privateTags || "")
            .split(",")
            .map((tag) => tag.trim())
            .filter(Boolean);

         if (idx >= 0 && idx < arr.length) {
            arr.splice(idx, 1);
         }
         return { ...prev, privateTags: arr.join(",") };
      });
   };

   return (
      <>
         {isCallManager ? (
            <button
               type="button"
               className="call-manager__action-button"
               onClick={() => setIsOpen(true)}
            >
               <div>
                  <Icon_Reschedule />
               </div>
               <span>{intl.formatMessage({ id: "callManager.reschedulePhoneCall" })}</span>
            </button>
         ) : (
            <Button
               buttonType="primary"
               className={"plan-message-modal__button"}
               onClick={() => setIsOpen(true)}
               title={displayedTime()}
               onMouseEnter={() => setHover(true)}
               onMouseLeave={() => setHover(false)}
               style={{
                  ...(areStylesBasedOnCaseStatus && {
                     backgroundColor: buttonStyles.dateBackground,
                     color: buttonStyles.dateText,
                     border: "none",
                     maxWidth: "100%",
                     filter: hover ? "brightness(0.95)" : "none",
                  }),
               }}
            >
               {visiblePlannedData.plannedTime ? (
                  <>
                     {visiblePlannedData.lockDate || visiblePlannedData.lockedDataAndTime ? (
                        <Icon_CasePlanned fill={buttonStyles.dateText} />
                     ) : (
                        <Icon_PlannedCalendar fill={buttonStyles.dateText} />
                     )}
                     <span className={"plan-message-modal__button__date"}>{displayedTime()}</span>
                  </>
               ) : (
                  <>
                     <Icon_Plan_Add fill="currentColor" stroke="none" />
                     <IntlMessages id="planner.plan" />
                  </>
               )}
            </Button>
         )}

         {isOpen ? (
            <Modal
               isOpen={isOpen}
               onCancel={setIsOpen}
               wrapClassName="plan-message-modal"
               width={560}
               height={575}
               customCloseIcon={
                  <Icon_Close
                     fill="none"
                     stroke="#64748B"
                     className="plan-message-modal__close-btn"
                  />
               }
               topBarContent={
                  <div className="plan-message-modal__header">
                     <Icon_CasePlan fill="#1C4387" stroke="#1C4387" />
                     <p className="p2 header-title">
                        <IntlMessages id="message.planning" />
                     </p>
                  </div>
               }
               footerContent={
                  <div className="plan-message-modal__buttons">
                     <Button onClick={() => setIsOpen(false)}>
                        <IntlMessages id="message.cancel" />
                     </Button>
                     <Button className="button-save" buttonType="primary" onClick={planMessage}>
                        <IntlMessages id="forms.save" />
                     </Button>
                  </div>
               }
            >
               <ShowDate
                  className="plan-message-modal__deadline"
                  date={deadlineDate}
                  dateType="deadline"
               />
               <div className="plan-message-modal__title-container">
                  <h5 className="plan-message-modal__title-text">{title}</h5>
               </div>
               <div className="plan-message-modal__next-day-info">
                  {displayedSuggestedDate &&
                     displayedSuggestedDate?.suggestedDate !==
                        plannedDetails.startDate?.format("YYYY-MM-DD") &&
                     !areSuggestedDatesFetched && (
                        <>
                           <Icon_Info
                              className="plan-message-modal__next-day-icon"
                              stroke={"#262626"}
                           />
                           <IntlMessages id="planner.nextDayInfo" />
                        </>
                     )}
               </div>
               <div className="plan-message-modal__planning-suggestion-container">
                  {suggestedDates.length === 0 || areSuggestedDatesFetched ? (
                     <Spin className="plan-message-modal__planning-suggestion-spinner" />
                  ) : (
                     <>
                        <Icon_ChevronLeft
                           fill="none"
                           stroke="var(--first)"
                           onClick={() => handleChangeSelectedSuggestedDate(-1)}
                           className={
                              `plan-message-modal__planning-suggestion-icon` +
                              (getDisplayedSuggestedDateIndex() === 0
                                 ? " plan-message-modal__planning-suggestion-icon__disabled"
                                 : "")
                           }
                        />

                        <div className="suggested-time">
                           <SuggestedDateInfo />

                           <Button
                              buttonType="primary"
                              className="planning-suggestion-button"
                              onClick={() => acceptSuggestedDate()}
                           >
                              <IntlMessages id="message.accept" />
                           </Button>
                        </div>
                        <Icon_ChevronRight
                           fill="none"
                           stroke="var(--first)"
                           onClick={() => handleChangeSelectedSuggestedDate(1)}
                           className={
                              `plan-message-modal__planning-suggestion-icon` +
                              (getDisplayedSuggestedDateIndex() === suggestedDates.length - 1
                                 ? " plan-message-modal__planning-suggestion-icon__disabled"
                                 : "")
                           }
                        />
                     </>
                  )}
               </div>
               <div className="plan-message-modal__main-planning-container">
                  <div className="main-planning-container__set-duration">
                     <p className="p2 duration">
                        <IntlMessages id="message.plan" />
                     </p>
                     <DatePicker
                        showTime
                        showTimeOnly
                        className="duration-picker"
                        format="HH:mm"
                        timeIntervals={10}
                        size="large"
                        value={moment().startOf("day").add(plannedDetails.duration, "minutes")}
                        onChange={(selectedTime) => {
                           const validTime =
                              selectedTime &&
                              moment.isMoment(selectedTime) &&
                              selectedTime.isValid()
                                 ? selectedTime
                                 : moment().startOf("day").add(effortTime, "minutes");
                           const durationInMinutes = validTime.diff(
                              moment().startOf("day"),
                              "minutes",
                           );
                           updatePlannedDetails(durationInMinutes, "duration", true);
                        }}
                        icon={<Icon_SandGlass fill="none" />}
                     />
                  </div>
                  <div className="main-planning-container__select-day">
                     <p className="p2 day">
                        <IntlMessages id="message.day" />
                     </p>
                     <DatePicker
                        className="day-picker"
                        value={plannedDetails.startDate || moment()}
                        format={"yyyy-MM-dd"}
                        size="large"
                        onChange={(e) => {
                           let value = moment(e);

                           if (!value.isValid()) {
                              value = moment();
                           }

                           updatePlannedDetails(value, "startDate", true);

                           if (
                              plannedDetails?.startDate &&
                              plannedDetails.startDate.format("YYYY-MM-DD") !==
                                 value.format("YYYY-MM-DD")
                           ) {
                              // here, when we change day, we set value in "hour-picker" to 08:00 (new day = starts hours from beginning of the day)
                              const updatedTime = value.clone().set("hour", 8).set("minute", 0);
                              updatePlannedDetails(updatedTime, "startTime");

                              setAreSuggestedDatesFetched(true);
                           }
                        }}
                     />
                     <div
                        className={`day-locker ${plannedDetails.lockDate ? "lock" : "unlock"}`}
                        onClick={() => updatePlannedDetails(!plannedDetails.lockDate, "lockDate")}
                     >
                        {plannedDetails.lockDate ? (
                           <Icon_Lock fill={"var(--first)"} />
                        ) : (
                           <Icon_Unlock />
                        )}
                     </div>
                  </div>
                  <div className="main-planning-container__select-hour">
                     <p className="p2 hour">
                        <IntlMessages id="message.hour" />
                     </p>
                     <DatePicker
                        showTime={true}
                        showTimeOnly={true}
                        className="hour-picker"
                        format="HH:mm"
                        timeIntervals={10}
                        size="large"
                        value={plannedDetails.startTime || moment()}
                        onChange={(e) => {
                           const value = e || moment();
                           updatePlannedDetails(value, "startTime");
                        }}
                        icon={<Icon_Clock fill="#CBD5E1" />}
                     />
                     <div
                        className={`hour-locker ${
                           plannedDetails.lockedDataAndTime ? "lock" : "unlock"
                        }`}
                        onClick={() =>
                           updatePlannedDetails(
                              !plannedDetails.lockedDataAndTime,
                              "lockedDataAndTime",
                           )
                        }
                     >
                        {plannedDetails.lockedDataAndTime ? (
                           <Icon_Lock fill={"var(--first)"} />
                        ) : (
                           <Icon_Unlock />
                        )}
                     </div>
                  </div>
               </div>
               <div className="plan-message-modal__time-used-container">
                  <p className="p2">
                     {" "}
                     <IntlMessages id="planner.plannedForToday" />
                  </p>{" "}
                  <p className="p2 time-used-value">{timeUsedToday}/8:00</p>{" "}
                  <p className="p2">
                     {" "}
                     <IntlMessages id="planner.hoursOfWork" />
                  </p>
               </div>
               <div className="plan-message-modal__automatic-tags-container">
                  {automaticTags && (
                     <>
                        <p className="p2">
                           <IntlMessages id="message.automaticTags" />
                        </p>
                        <TextArea
                           className="plan-message-modal-textarea"
                           defaultValue={plannedDetails.tags}
                           disabled
                        ></TextArea>
                     </>
                  )}
               </div>
               <div className="plan-message-modal__private-tags-container">
                  <div className="tags-wrapper">
                     <p className="p2">
                        <IntlMessages id="message.privateTags" />
                     </p>
                     <Button
                        className="automatic-private-tags-button"
                        buttonType="third"
                        onClick={showTags}
                     >
                        <IntlMessages id="message.automaticTags" />
                     </Button>
                  </div>
                  <div className="tags-container">
                     {tagsToArray.map((tag, idx) => {
                        const isEditing = editingIdx === idx;

                        return (
                           <div key={`${tag}-${idx}`} className="tags-container__tag-element">
                              {!isEditing ? (
                                 <div style={{ position: "relative" }}>
                                    <button
                                       className="tags-container__tag-element__button"
                                       type="button"
                                       onClick={() => handleEdit(tag, idx)}
                                    >
                                       {tag}
                                    </button>
                                    <div className="tags-container__tag-element__button-remove-container">
                                       <Button
                                          buttonType="danger"
                                          onClick={() => removePrivateTag(idx)}
                                       >
                                          <Icon_Close fill="none" stroke="white" />
                                       </Button>
                                    </div>
                                 </div>
                              ) : (
                                 <div
                                    className={clsx("tags-container", isEditing ? "small-gap" : "")}
                                 >
                                    <Input
                                       className="plan-message-modal-tags"
                                       value={tagValue}
                                       onChange={(e) => setTagValue(e.target.value)}
                                       onKeyDown={(e) => {
                                          if (e.key === "Enter") savePrivateTag();
                                          if (e.key === "Escape") {
                                             setEditingIdx(null);
                                             setTagValue("");
                                          }
                                       }}
                                       allowClear
                                       autoFocus
                                       refreshOnValueChange
                                    />
                                    <Button
                                       className="tags-container__action-button"
                                       buttonType="primary"
                                       onClick={savePrivateTag}
                                    >
                                       <Icon_Check stroke="white" fill="transparent" />
                                    </Button>
                                 </div>
                              )}
                           </div>
                        );
                     })}
                     <div className="tags-container__add-element">
                        {openAddTagInput && (
                           <div className="tags-container">
                              <Input
                                 className="plan-message-modal-tags"
                                 value={tagValue}
                                 onChange={(e) => setTagValue(e.target.value)}
                                 onKeyDown={(e) => {
                                    if (e.key === "Enter") savePrivateTag();
                                    if (e.key === "Escape") {
                                       setOpenAddTagInput(false);
                                       setTagValue("");
                                    }
                                 }}
                                 allowClear
                                 autoFocus
                              />
                           </div>
                        )}
                        {editingIdx === null && (
                           <Button
                              className="tags-container__action-button"
                              buttonType="primary"
                              onClick={() =>
                                 openAddTagInput ? savePrivateTag() : setOpenAddTagInput(true)
                              }
                           >
                              {openAddTagInput ? (
                                 <Icon_Check stroke="white" fill="transparent" />
                              ) : (
                                 <Icon_Plus />
                              )}
                           </Button>
                        )}
                     </div>
                  </div>
               </div>

               <div className="plan-message-modal__comments-container">
                  <p className="p2">
                     <IntlMessages id="message.comment" />
                  </p>
                  <AddComments
                     /* @ts-ignore */
                     className="plan-message-modal__add-comment"
                     modalMode={true}
                     onSendComment={onSendComment}
                     actionId={messageId}
                  />
               </div>
            </Modal>
         ) : null}
      </>
   );
};

export default PlanMessageModal;
